import { createAsyncThunk } from "@reduxjs/toolkit";
import { iStateMessage } from "@/customTypes/GenericReduxTypes";
import { iGenericResponse } from "@/customTypes/CommonServiceTypes";
import { curryGetThunkName } from "@/projectUtils/ReduxUtil";
import { httpStatusCodes } from "@/customTypes/NetworkTypes";
import { shiprocketService } from "@/services/api/cresivoxServer/shiprocketService";
import { iShiprocketDTO, REDUCER_NAME } from "./Types";

const curriedGetThunkName = curryGetThunkName(REDUCER_NAME);

export const createShiprocketUser = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { data: iShiprocketDTO },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("createShiprocketUser"),
  //@ts-expect-error
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await shiprocketService.DataShiprocketUser(data);
      if (response) {
        const { httpStatusCode, data: responseData, message } = response;
        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (responseData && responseData.data) {
              return {
                data: {
                  shiprocketUser: responseData.data,
                  message,
                },
              };
            } else {
              return rejectWithValue({ httpStatusCode, message });
            }
          }
          default: {
            return rejectWithValue({ httpStatusCode, message });
          }
        }
      } else {
        return rejectWithValue({ message: "No response received" });
      }
    } catch (error) {
      console.error("Error in createShiprocketUser:", error);
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const getShiprocketUser = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { vendorId: string | number },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("getShiprocketUser"),
  //@ts-expect-error
  async ({ vendorId }, { rejectWithValue }) => {
    try {
      const response = await shiprocketService.getShiprocketUser(vendorId);
      if (response) {
        const { httpStatusCode, data, message } = response;
        switch (httpStatusCode) {
          case httpStatusCodes.SUCCESS_OK: {
            if (data && data.data) {
              return {
                data: {
                  data: data.data,
                  message,
                },
              };
            } else {
              return rejectWithValue({ httpStatusCode, message });
            }
          }
          default: {
            return rejectWithValue({ httpStatusCode, message });
          }
        }
      } else {
        return rejectWithValue({ message: "No response received" });
      }
    } catch (error) {
      console.error("Error in getShiprocketUser:", error);
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);

export const createShipRocket = createAsyncThunk<
  iGenericResponse<any | null> | null,
  { vendorId: string | number; orderPayload: object },
  { rejectValue: iStateMessage }
>(
  curriedGetThunkName("createShipRocket"),
  // @ts-expect-error
  async ({ vendorId, orderPayload }, { rejectWithValue }) => {
    try {
      const response = await shiprocketService.createShipRocket(vendorId, orderPayload);
      if (response) {
        const { httpStatusCode, data, message } = response;
        if (httpStatusCode === httpStatusCodes.SUCCESS_OK && data?.data) {
          return { data: { data: data.data, message } };
        } else {
          return rejectWithValue({ httpStatusCode, message });
        }
      } else {
        return rejectWithValue({ message: "No response received" });
      }
    } catch (error) {
      console.error("Error in createShipRocket:", error);
      return rejectWithValue({ message: "Something went wrong" });
    }
  }
);


export const getShipRocketDetail = createAsyncThunk<
  iGenericResponse<any | null> | null,
  string, // ✅ Only orderId as argument
  { rejectValue: iStateMessage }
>(
  "shiprocket/getOrderDetails",
  // @ts-expect-error
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await shiprocketService.getShipRocketDetail(orderId); // orderId is used in URL

      if (!response) {
        return rejectWithValue({
          message: "No response received",
            // @ts-expect-error
          httpStatusCode: httpStatusCodes.BAD_REQUEST,
        });
      }

      const { httpStatusCode, data, message } = response;

      if (httpStatusCode === httpStatusCodes.SUCCESS_OK && data?.data) {
        return {
          data: {
            data: data.data,
            message,
          },
        };
      }

      return rejectWithValue({
        httpStatusCode,
        message: message || "Failed to get ShipRocket details",
      });
    } catch (error) {
      return rejectWithValue({
        message: error instanceof Error ? error.message : "Something went wrong",
          // @ts-expect-error
        httpStatusCode: httpStatusCodes.INTERNAL_SERVER_ERROR,
      });
    }
  }
);

export const shipRocketOrderTrack = createAsyncThunk<
  iGenericResponse<any | null> | null,
  string, // ✅ Only orderId as argument
  { rejectValue: iStateMessage }
>(
  "shiprocket/trackOrder",
  // @ts-expect-error
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await shiprocketService.shipRocketOrderTrack(orderId); // orderId is used in URL

      if (!response) {
        return rejectWithValue({
          message: "No response received",
            // @ts-expect-error
          httpStatusCode: httpStatusCodes.BAD_REQUEST,
        });
      }

      const { httpStatusCode, data, message } = response;

      if (httpStatusCode === httpStatusCodes.SUCCESS_OK && data?.data) {
        return {
          data: {
            data: data.data,
            message,
          },
        };
      }

      return rejectWithValue({
        httpStatusCode,
        message: message || "Failed to get ShipRocket details",
      });
    } catch (error) {
      return rejectWithValue({
        message: error instanceof Error ? error.message : "Something went wrong",
          // @ts-expect-error
        httpStatusCode: httpStatusCodes.INTERNAL_SERVER_ERROR,
      });
    }
  }
);
